import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { Page } from 'components/Page';
import { PageHeader } from 'components/PageHeader';
import { ScrollView } from 'components/ScrollView';
import { useConfirmUnsaved } from 'hooks/useConfirm';
import { useEsc } from 'hooks/useEsc';
import React from 'react';
import { useDispatch } from 'react-redux';
import { NAVIGATE_MODAL_BACK } from 'store/actions';
import { doTaskAssignedStatusSave } from 'store/tasks/actions';

export const TasksAssignedStatusModal: React.FC<
  ModalScreenProps<ModalScreens.TasksAssignedStatus>
> = ({ navigation, route }) => {
  const dispatch = useDispatch();
  const confirmUnsaved = useConfirmUnsaved();
  const { taskId, activeStatus, availableStatuses } = route.params;
  const [selectedStatusId, setSelectedStatusId] = React.useState(
    activeStatus.statusId,
  );

  const isDirty = selectedStatusId !== activeStatus.statusId;

  React.useEffect(() => {
    navigation?.setOptions({ gestureEnabled: !isDirty });
  }, [isDirty, navigation]);

  const selectedStatus = React.useMemo(
    () =>
      availableStatuses.find((status) => status.statusId === selectedStatusId),
    [availableStatuses, selectedStatusId],
  );

  const handleCloseWithoutSaving = React.useCallback(() => {
    dispatch(NAVIGATE_MODAL_BACK([ModalScreens.TasksAssignedStatus]));
  }, [dispatch]);

  const handleSave = React.useCallback(() => {
    if (!selectedStatus || !isDirty) {
      return;
    }
    dispatch(doTaskAssignedStatusSave({ taskId, status: selectedStatus }));
  }, [dispatch, isDirty, selectedStatus, taskId]);

  const handleClose = React.useCallback(() => {
    if (isDirty) {
      confirmUnsaved({
        onSave: handleSave,
        onDiscard: handleCloseWithoutSaving,
      });
      return;
    }
    handleCloseWithoutSaving();
  }, [confirmUnsaved, handleCloseWithoutSaving, handleSave, isDirty]);

  useEsc(handleClose);

  return (
    <Page
      header={
        <PageHeader
          title="Task Status"
          onPressCancelButton={handleClose}
          onPressSaveButton={isDirty ? handleSave : null}
        />
      }
    >
      <ScrollView>
        <Card marginHorizontal label="Status">
          {availableStatuses.map((status) => (
            <CardRow
              key={status.statusId}
              label={status.statusName}
              onPress={() => setSelectedStatusId(status.statusId)}
              radioValue={status.statusId === selectedStatusId}
            />
          ))}
        </Card>
      </ScrollView>
    </Page>
  );
};
